import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

/**
 * StatusBadge – Universal Status Display Component (MANDATORY)
 *
 * Global UI Design System Enforcement:
 * - Semantic color mapping for all statuses
 * - Auto-converts to sentence case
 * - Consistent 11px font size
 * - Replaces all inline badge styling
 *
 * Semantic Color Mapping:
 *   success → Active, Approved, Completed, Enabled, Verified, Paid
 *   warning → Pending, Suspended, In Progress, Review, Inactive, Disabled
 *   error   → Rejected, Failed, Expired, Blocked, Error
 *   info    → Published, Archived, Closed
 *   primary → Draft, New
 *   muted   → Unknown / low-emphasis fallback
 *
 * Usage:
 *   <StatusBadge status="ACTIVE" />                    // Auto-maps to success
 *   <StatusBadge status="pending" />                   // Auto-maps to warning
 *   <StatusBadge status="Custom" variant="info" />    // Force specific variant
 */

type StatusVariant = 'success' | 'warning' | 'error' | 'info' | 'muted' | 'primary' | 'default';

/** Maps status strings to semantic variants */
const STATUS_MAP: Record<string, StatusVariant> = {
  // Success statuses
  active: 'success',
  approved: 'success',
  completed: 'success',
  enabled: 'success',
  verified: 'success',
  paid: 'success',
  confirmed: 'success',
  resolved: 'success',
  
  // Warning statuses
  pending: 'warning',
  pending_approval: 'warning',
  pending_registration_approval: 'warning',
  pending_ccm_approval: 'warning',
  suspended: 'warning',
  'in-progress': 'warning',
  in_progress: 'warning',
  inprogress: 'warning',
  review: 'warning',
  processing: 'warning',
  waiting: 'warning',
  inactive: 'warning',
  disabled: 'warning',
  
  // Error statuses
  rejected: 'error',
  error: 'error',
  failed: 'error',
  expired: 'error',
  blocked: 'error',
  cancelled: 'error',
  denied: 'error',
  
  // Info statuses
  published: 'info',
  open: 'info',
  archived: 'info',
  closed: 'info',
  submitted: 'info',
  new: 'primary',
  
  // Muted statuses
  draft: 'primary',
  unknown: 'muted',
};

const statusBadgeVariants = cva(
  'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-medium leading-none transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
  {
    variants: {
      variant: {
        success: 'border-success/30 bg-success-soft text-success dark:border-success/20',
        warning: 'border-warning/30 bg-warning-soft text-warning dark:border-warning/20',
        error: 'border-destructive/30 bg-destructive-soft text-destructive dark:border-destructive/20',
        info: 'border-info/30 bg-info-soft text-info dark:border-info/20',
        muted: 'border-border bg-muted text-muted-foreground dark:border-border dark:bg-muted dark:text-muted-foreground',
        primary: 'border-primary/30 bg-primary-soft text-primary dark:border-primary/20',
        default: 'border-transparent bg-primary text-primary-foreground',
      },
    },
    defaultVariants: {
      variant: 'info',
    },
  }
);

/** Converts raw status string to sentence case */
function toSentenceCase(str: string): string {
  if (!str) return '';
  const cleaned = str.replace(/[_-]/g, ' ');
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1).toLowerCase();
}

/** Resolves status string to variant */
function resolveVariant(status: string): StatusVariant {
  const normalized = status.toLowerCase().replace(/\s+/g, '_');
  return STATUS_MAP[normalized] ?? 'info';
}

export interface StatusBadgeProps
  extends Omit<React.HTMLAttributes<HTMLSpanElement>, 'children'>,
    VariantProps<typeof statusBadgeVariants> {
  /** Status string to display (e.g., "ACTIVE", "pending", "In Progress") */
  status: string;
  /** Force a specific variant instead of auto-detecting from status */
  variant?: StatusVariant;
  /** Custom label to display instead of status text */
  label?: string;
  /** Show a leading dot indicator */
  dot?: boolean;
}

export function StatusBadge({
  status,
  variant,
  label,
  dot = false,
  className,
  ...props
}: StatusBadgeProps) {
  const resolvedVariant = variant ?? resolveVariant(status);
  const displayText = label ?? toSentenceCase(status);

  return (
    <span
      className={cn(statusBadgeVariants({ variant: resolvedVariant }), className)}
      {...props}
    >
      {dot && (
        <span
          className={cn(
            'w-1.5 h-1.5 rounded-full shrink-0',
            resolvedVariant === 'success' && 'bg-success',
            resolvedVariant === 'warning' && 'bg-warning',
            resolvedVariant === 'error' && 'bg-destructive',
            resolvedVariant === 'info' && 'bg-info',
            resolvedVariant === 'primary' && 'bg-primary',
            resolvedVariant === 'muted' && 'bg-muted-foreground/50',
            resolvedVariant === 'default' && 'bg-primary-foreground'
          )}
        />
      )}
      {displayText}
    </span>
  );
}

StatusBadge.displayName = 'StatusBadge';

/**
 * Helper to get status variant for conditional styling
 * Useful when you need to apply colors elsewhere (e.g., icons, borders)
 */
export function getStatusVariant(status: string): StatusVariant {
  return resolveVariant(status);
}

export { statusBadgeVariants };
export type { StatusVariant };
export default StatusBadge;
